import React, { useState, useRef, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  Loader2, 
  Calculator, 
  FileUp, 
  RefreshCw, 
  Sparkles, 
  CheckCircle2, 
  AlertCircle,
  X,
  ArrowRight,
  School
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { extractArrearData, ExtractedArrearData } from '../src/utils/arrearEngine';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addWatermarkToPdf } from '../lib/pdfUtils';
import { db } from '../src/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { DEDUCTOR_DATA } from '../constants/deductorData';
import { LoadingOverlay } from './LoadingOverlay';
import { saveUsageData } from '../services/usageService';

export const TdsFormView: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ExtractedArrearData | null>(null);
  const [schoolName, setSchoolName] = useState('');
  const [joiningDate, setJoiningDate] = useState('');
  const [financialYear, setFinancialYear] = useState('2025-26');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'full'>('summary');
  const previewRef = useRef<HTMLDivElement>(null);

  const [deductorInfo, setDeductorInfo] = useState<any>(null);

  const formatDeductorValue = (value: string | undefined) => {
    if (!value || value === 'Not Found') return '';
    return value;
  };

  useEffect(() => {
    const fetchDeductor = async () => {
      if (!data?.profile.district) return;
      const docRef = doc(db, 'deductor', data.profile.district.toUpperCase());
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setDeductorInfo(docSnap.data());
      } else {
        // Fallback
        setDeductorInfo({
          address: `District Programme Officer, Establishment, ${data.profile.district}`,
          pan: "Not Found",
          tan: "Not Found"
        });
      }
    };
    fetchDeductor();
  }, [data?.profile.district]);

  // Re-calculate MA and PTAX if joiningDate or financialYear changes after extraction
  React.useEffect(() => {
    if (data) {
      const updatedWithMA = data.monthlyDrawn.map(m => {
        let ma = m.ma;
        if (m.pay > 0) {
          if (joiningDate) {
            try {
              const jDate = new Date(joiningDate);
              if (!isNaN(jDate.getTime())) {
                const jMonth = jDate.getMonth();
                const jYear = jDate.getFullYear();
                const jDay = jDate.getDate();

                const monthNames = [
                  "January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"
                ];
                const currentMonthIndex = monthNames.indexOf(m.month);
                
                const [startYearStr, endYearStr] = financialYear.split('-');
                const startYear = parseInt(startYearStr);
                const endYear = 2000 + parseInt(endYearStr);
                const currentYear = (currentMonthIndex >= 2) ? startYear : endYear;

                if (currentYear === jYear && currentMonthIndex === jMonth) {
                  const daysInMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate();
                  const daysWorked = daysInMonth - jDay + 1;
                  ma = Math.round((1000 / daysInMonth) * daysWorked);
                } else if (currentYear > jYear || (currentYear === jYear && currentMonthIndex > jMonth)) {
                  ma = 1000;
                } else {
                  ma = 0;
                }
              }
            } catch (e) {
              console.error("Error re-calculating MA:", e);
            }
          } else {
            ma = 1000;
          }
        } else {
          ma = 0;
        }
        const gis = m.pay > 0 ? 30 : 0;
        return { ...m, ma, gis };
      });

      // Calculate Annual Income for PTAX
      const annualIncome = updatedWithMA.reduce((sum, m) => {
        return sum + (m.pay + m.da + m.hra + m.ma + (m.cta || 0));
      }, 0);

      // Determine Annual PTAX
      let annualPTAX = 0;
      if (annualIncome > 1000000) {
        annualPTAX = 2500;
      } else if (annualIncome > 500000) {
        annualPTAX = 2000;
      } else if (annualIncome > 300000) {
        annualPTAX = 1000;
      }

      // Apply PTAX only to September
      const updatedMonthlyDrawn = updatedWithMA.map(m => {
        const ptax = m.month === 'September' ? annualPTAX : 0;
        return { ...m, ptax };
      });

      // Only update if there's a change to avoid infinite loop
      const hasChanged = JSON.stringify(updatedMonthlyDrawn) !== JSON.stringify(data.monthlyDrawn);
      if (hasChanged) {
        setData({ ...data, monthlyDrawn: updatedMonthlyDrawn });
      }
    }
  }, [joiningDate, financialYear]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a valid PDF file.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const extracted = await extractArrearData(file, true, joiningDate, financialYear);
      setData(extracted);
      setIsProcessing(false);
    } catch (err: any) {
      setError(err.message || 'Error processing PDF.');
      setIsProcessing(false);
    }
  };

  const generatePDF = (download: boolean = true) => {
    if (!data) return;
    
    const district = data.profile.district || "Gopalganj";
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const contentWidth = pageWidth - (margin * 2);

    // --- PAGE 1: T.D.S. CERTIFICATE ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.rect(margin, 10, 30, 10);
    doc.text("Form No. - 16", margin + 2, 14);
    doc.setFontSize(7);
    doc.text("[See Rule 31(1) (a)]", margin + 2, 18);

    doc.setFontSize(24);
    doc.text("T.D.S.", pageWidth / 2, 18, { align: 'center' });

    doc.setFontSize(10);
    doc.text("Certificate under Section 203 of the Income-Tax Act. 1961 for tax deducted at source from income", pageWidth / 2, 25, { align: 'center' });
    doc.text("chargeable under the head \"SALARIES\"", pageWidth / 2, 30, { align: 'center' });

    // Table 1: Employer/Employee
    let currentY = 35;
    doc.rect(margin, currentY, contentWidth / 2, 5);
    doc.setFontSize(8);
    doc.text("Name and address of the Employer", margin + 2, currentY + 3.5);
    doc.rect(margin + contentWidth / 2, currentY, contentWidth / 2, 5);
    doc.text("Name and designation of the Employee", margin + contentWidth / 2 + 2, currentY + 3.5);
    
    currentY += 5;
    doc.rect(margin, currentY, contentWidth / 2, 15);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDeductorValue(deductorInfo?.address), margin + 2, currentY + 8);
    doc.rect(margin + contentWidth / 2, currentY, contentWidth / 2, 15);
    doc.text(data.profile.name.toUpperCase(), margin + contentWidth / 2 + 2, currentY + 4);
    doc.setFont('helvetica', 'normal');
    doc.text(data.profile.designation.toUpperCase(), margin + contentWidth / 2 + 2, currentY + 8);
    if (schoolName) {
      doc.text(schoolName.toUpperCase(), margin + contentWidth / 2 + 2, currentY + 12);
    }

    // Table 2: PAN/TAN
    currentY += 15;
    doc.rect(margin, currentY, contentWidth / 3, 5);
    doc.text("PAN NO.OF THE DEDUCTOR", margin + 2, currentY + 3.5);
    doc.rect(margin + contentWidth / 3, currentY, contentWidth / 3, 5);
    doc.text("TAN NO. OF DEDUCTOR", margin + contentWidth / 3 + 2, currentY + 3.5);
    doc.rect(margin + (contentWidth / 3) * 2, currentY, contentWidth / 3, 5);
    doc.text("PAN NO.OF THE EMPLOYEE", margin + (contentWidth / 3) * 2 + 2, currentY + 3.5);

    currentY += 5;
    doc.rect(margin, currentY, contentWidth / 3, 5);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDeductorValue(deductorInfo?.pan), margin + contentWidth / 6, currentY + 3.5, { align: 'center' });
    doc.rect(margin + contentWidth / 3, currentY, contentWidth / 3, 5);
    doc.text(formatDeductorValue(deductorInfo?.tan), margin + contentWidth / 2, currentY + 3.5, { align: 'center' });
    doc.rect(margin + (contentWidth / 3) * 2, currentY, contentWidth / 3, 5);
    doc.text(data.profile.pan, margin + (contentWidth / 6) * 5, currentY + 3.5, { align: 'center' });

    // Table 3: FY/AY
    currentY += 5;
    doc.rect(margin, currentY, contentWidth / 2, 5);
    doc.setFont('helvetica', 'normal');
    doc.text("Financial Year-2025-26", margin + contentWidth / 4, currentY + 3.5, { align: 'center' });
    doc.rect(margin + contentWidth / 2, currentY, contentWidth / 2, 5);
    doc.text("Assessment Year -2026-27", margin + (contentWidth / 4) * 3, currentY + 3.5, { align: 'center' });

    // Table 4: Quarter Acknowledgement
    currentY += 5;
    doc.rect(margin, currentY, contentWidth / 4, 10);
    doc.text("Quarter", margin + contentWidth / 8, currentY + 6, { align: 'center' });
    doc.rect(margin + contentWidth / 4, currentY, contentWidth / 4, 10);
    doc.text("Acknowledgement No.", margin + (contentWidth / 8) * 3, currentY + 6, { align: 'center' });
    
    doc.rect(margin + contentWidth / 2, currentY, contentWidth / 2, 5);
    doc.text("Period", margin + (contentWidth / 4) * 3, currentY + 3.5, { align: 'center' });
    doc.rect(margin + contentWidth / 2, currentY + 5, contentWidth / 4, 5);
    doc.text("From", margin + (contentWidth / 8) * 5, currentY + 8.5, { align: 'center' });
    doc.rect(margin + (contentWidth / 4) * 3, currentY + 5, contentWidth / 4, 5);
    doc.text("To", margin + (contentWidth / 8) * 7, currentY + 8.5, { align: 'center' });
    
    currentY += 10;
    const qRows = ["24Q I", "24Q II", "24Q III", "24Q IV"];
    qRows.forEach((q, i) => {
      doc.rect(margin, currentY + (i * 5), contentWidth / 4, 5);
      doc.text(q, margin + 2, currentY + (i * 5) + 3.5);
      doc.rect(margin + contentWidth / 4, currentY + (i * 5), contentWidth / 4, 5);
    });
    
    doc.rect(margin + contentWidth / 2, currentY, contentWidth / 4, 20);
    doc.setFont('helvetica', 'bold');
    doc.text("Mar-25", margin + (contentWidth / 8) * 5, currentY + 11, { align: 'center' });
    doc.rect(margin + (contentWidth / 4) * 3, currentY, contentWidth / 4, 20);
    doc.text("Feb-26", margin + (contentWidth / 8) * 7, currentY + 11, { align: 'center' });
    
    currentY += 20;
    doc.rect(margin + contentWidth / 2, currentY, contentWidth / 2, 5);
    doc.text("Assessment Year", margin + (contentWidth / 2) + 2, currentY + 3.5);
    doc.text("2026-27", margin + (contentWidth / 4) * 3.5, currentY + 3.5, { align: 'center' });
    doc.setFont('helvetica', 'normal');

    currentY += 5;
    doc.rect(margin, currentY, contentWidth, 5);
    doc.setFont('helvetica', 'bold');
    doc.text("DETAILS OF SALARY PAID AND ANY OTHER INCOME AND TAX DEDUCTED", pageWidth / 2, currentY + 3.5, { align: 'center' });

    // Section A: Income Details
    currentY += 5;
    const grossSalary = data.monthlyDrawn.reduce((acc, m) => acc + (m.pay + m.da + m.hra + m.ma + m.cta), 0);
    const stdDed = 75000;
    const ptax = data.monthlyDrawn.reduce((acc, m) => acc + m.ptax, 0);
    const taxableIncome = Math.max(0, grossSalary - stdDed - ptax);
    
    const taxOnTotal = Math.round(taxableIncome > 700000 ? (taxableIncome - 700000) * 0.1 : 0); 
    const taxRebate = taxOnTotal; 
    const taxAfterRebate = taxOnTotal - taxRebate;
    const totalTaxPaid = data.monthlyDrawn.reduce((acc, m) => acc + m.itax, 0);

    const leftCol = [
      ["A", "Whether option for taxation u/s 115BAC?", "YES"],
      ["1", "GROSS SALARY", `Rs. ${grossSalary.toFixed(2)}`],
      ["2", "(a) LESS : Allowance to the extent exempt Under Section 10 (13A)", "Rs. 0.00"],
      ["", "(b) LESS : Allowance to the extent exempt Under Section 10 (14)(i)", "Rs. 0.00"],
      ["3", "BALANCE (1-2)", `Rs. ${grossSalary.toFixed(2)}`],
      ["4", "ADD: ANY OTHER INCOME REPORTED BY THE EMPLOYEE", "Rs. 0.00"],
      ["5", "GROSS TOTAL INCOME (3+4)", `Rs. ${grossSalary.toFixed(2)}`],
      ["6", "UNDER SECTION 24(1) DEDUCTION(H.B.A INT +P.TAX)", `Rs. ${ptax.toFixed(2)}`],
      ["7", "STANDARD DEDUCTION", stdDed.toFixed(2)],
      ["8", "INCOME CHARGEABLE UNDER THE HEAD 'SALARIES'(5-6-7)", taxableIncome.toFixed(2)],
      ["9", "Deductions under chapter VI-A", "Rs. 0.00"],
      ["", "(A) Sections 80C, 80CCC and 80 CCD", "Rs. 0.00"],
      ["", "  (a) Section 80 C", "Rs. 0.00"],
      ["", "    (i) GPF/PRAN", "Rs. 0.00"],
      ["", "    (ii) LIC (including G.I.)", "Rs. 0.00"],
      ["", "    (iii) Tuition fee", "Rs. 0.00"],
      ["", "    (iv) Repayment of HBA", "Rs. 0.00"],
      ["", "    (v) NSC/PPF", "Rs. 0.00"],
      ["", "    (vi) LIC Primium", "Rs. 0.00"],
      ["", "    (vii) Sukanya/P.O Insurance", "Rs. 0.00"],
      ["", "    (viii) Total section 80 C", "Rs. 0.00"],
      ["", "  (b) Sections 80 CCC", "Rs. 0.00"],
      ["", "  (c) Sections 80 CCD", "Rs. 0.00"],
      ["", "(B) Under Sections Chapter VI-A", "Rs. 0.00"],
      ["", "Section 80 CCD (1B)", "Rs. 0.00"],
      ["", "Section 80 U", "Rs. 0.00"],
      ["10", "AGGREGATE OF DEDUCATIBLE AMOUNT VI-A (A+B)", "Rs. 0.00"]
    ];

    const rightCol = [
      ["11", "TOTAL INCOME (8-10)", `Rs. ${taxableIncome.toFixed(2)}`],
      ["12", "TOTAL INCOME", `Rs. ${taxableIncome.toFixed(2)}`],
      ["13", "TAX ON TOTAL INCOME", `Rs. ${taxOnTotal.toFixed(2)}`],
      ["14", "TAX REBATE U/S 87 A", `Rs. ${taxRebate.toFixed(2)}`],
      ["15", "TAX AFTER REBATE", `Rs. ${taxAfterRebate.toFixed(2)}`],
      ["16", "EDUCATION & HEALTH CESS", "Rs. 0.00"],
      ["17", "TOTAL", "Rs. 0.00"],
      ["18", "RELIEF U/S 89(I)", "Rs. 0.00"],
      ["19", "TOTAL PAYABLE TAX", "Rs. 0.00"],
      ["20", "TAX DEDUCTED AT SOURCE", `Rs. ${totalTaxPaid.toFixed(2)}`],
      ["21", "TAX PAYABLE/REFUNDABLE", "Rs. 0.00"]
    ];

    doc.setFontSize(6);
    const rowHeight = 5;
    leftCol.forEach((row, i) => {
      const y = currentY + (i * rowHeight);
      doc.rect(margin, y, 5, rowHeight);
      doc.text(row[0], margin + 2.5, y + 3.8, { align: 'center' });
      doc.rect(margin + 5, y, contentWidth / 2 - 25, rowHeight);
      doc.text(row[1], margin + 7, y + 3.8);
      doc.rect(margin + contentWidth / 2 - 20, y, 20, rowHeight);
      doc.text(row[2], margin + contentWidth / 2 - 10, y + 3.8, { align: 'center' });
    });

    rightCol.forEach((row, i) => {
      const y = currentY + (i * rowHeight);
      doc.rect(margin + contentWidth / 2, y, 5, rowHeight);
      doc.text(row[0], margin + contentWidth / 2 + 2.5, y + 3.8, { align: 'center' });
      doc.rect(margin + contentWidth / 2 + 5, y, contentWidth / 2 - 25, rowHeight);
      doc.text(row[1], margin + contentWidth / 2 + 7, y + 3.8);
      doc.rect(margin + contentWidth - 20, y, 20, rowHeight);
      doc.text(row[2], margin + contentWidth - 10, y + 3.8, { align: 'center' });
    });

    // Table: Details of Tax Deducted
    const taxTableStartY = currentY + (rightCol.length * rowHeight) + 2;
    doc.rect(margin + contentWidth / 2, taxTableStartY, contentWidth / 2, 5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text("DETAILS OF TAX DEDUCTED", margin + (contentWidth / 4) * 3, taxTableStartY + 3.5, { align: 'center' });
    
    const taxTableHead = [['Sn. No.', 'TDS', 'Education Cess', 'Total', 'Date of Tax Deduction']];
    const taxTableBody = data.monthlyDrawn.map((m, i) => [
      (i + 1).toString(),
      m.itax > 0 ? m.itax.toFixed(2) : "0.00",
      "0.00",
      m.itax > 0 ? m.itax.toFixed(2) : "0.00",
      m.month.substring(0, 3) + "-" + (m.month === "January" || m.month === "February" ? "26" : "25")
    ]);
    taxTableBody.push(["Total", totalTaxPaid.toFixed(2), "0.00", totalTaxPaid.toFixed(2), ""]);

    autoTable(doc, {
      startY: taxTableStartY + 5,
      margin: { left: margin + contentWidth / 2 },
      tableWidth: contentWidth / 2,
      head: taxTableHead,
      body: taxTableBody,
      theme: 'grid',
      styles: { fontSize: 5, cellPadding: 0.8, halign: 'center' },
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], lineWidth: 0.1, fontStyle: 'bold' },
    });

    // Footer
    const pageHeight1 = doc.internal.pageSize.getHeight();
    currentY = currentY + (leftCol.length * rowHeight) + 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    
    const dpoName = deductorInfo?.dpoName || "..................................";
    const dpoFatherName = deductorInfo?.dpoFatherName || "..................................";
    const dpoAddress = deductorInfo?.address || `District Programme Officer, Establishment, ${district}`;
    
    const certText = `I, ${dpoName}, Son of Sri ${dpoFatherName}, ${dpoAddress} do hereby Certify that a sum of Rs. ${totalTaxPaid.toFixed(2)} has been deducted at source and paid to the credit of the Central Govt. I further Certify that the information given above is true and Correct based on the books of account, document and available records.`;
    
    const wrappedCertText = doc.splitTextToSize(certText, pageWidth - 2 * margin);
    doc.text(wrappedCertText, margin, currentY);
    
    doc.setFont('helvetica', 'bold');
    doc.text("Signature of Employee", margin + 10, pageHeight1 - 25);
    doc.text(`District Programme Officer, Establishment, ${district}`, pageWidth - margin - 70, pageHeight1 - 25, { align: 'left' });

    // --- PAGE 2: INCOME STATEMENT ---
    doc.addPage();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text("INCOME STATEMENT OF SALARIED EMPLOYEE FOR TAX CALCULATION", pageWidth / 2, 15, { align: 'center' });
    doc.line(margin, 17, pageWidth - margin, 17);
    
    doc.setFontSize(10);
    doc.text("Financial Year -2025-26", margin + 20, 22);
    doc.text("Assessment Year -2026-27", pageWidth - margin - 60, 22);
    doc.line(margin, 24, pageWidth - margin, 24);

    doc.rect(margin, 24, contentWidth, 22);
    doc.text("Name of Employee", margin + 2, 30);
    doc.text(data.profile.name.toUpperCase(), margin + 40, 30);
    doc.text("PAN NO. OF THE EMPLOYEE -", margin + 110, 30);
    doc.text(data.profile.pan, margin + 165, 30);
    
    doc.line(margin, 32, pageWidth - margin, 32);
    doc.text("Designation", margin + 2, 38);
    const designationText = `${data.profile.designation.toUpperCase()} ${schoolName ? `(${schoolName.toUpperCase()})` : ''}`;
    const wrappedDesignation = doc.splitTextToSize(designationText, 65);
    doc.text(wrappedDesignation, margin + 40, 38);
    doc.text(`Treasury Office -${district.toUpperCase()}`, margin + 110, 38);

    // Monthly Salary Table
    const salaryTableHead = [['Month', 'Pay', 'D.A.', 'H.R.A.', 'M.A.', 'C.T.A.', 'Total', 'P.F/NPS', 'P. TAX', 'G.I.S', 'I.Tax']];
    const salaryTableBody = data.monthlyDrawn.map((m, i) => [
      m.month.substring(0, 3) + "-" + (m.month === "January" || m.month === "February" ? "26" : "25"),
      m.pay.toFixed(2),
      m.da.toFixed(2),
      m.hra.toFixed(2),
      m.ma.toFixed(2),
      m.cta.toFixed(2),
      (m.pay + m.da + m.hra + m.ma + m.cta).toFixed(2),
      m.nps.toFixed(2),
      m.ptax.toFixed(2),
      m.gis.toFixed(2),
      m.itax.toFixed(2)
    ]);
    
    const totals = data.monthlyDrawn.reduce((acc, m) => ({
      pay: acc.pay + m.pay,
      da: acc.da + m.da,
      hra: acc.hra + m.hra,
      ma: acc.ma + m.ma,
      cta: acc.cta + m.cta,
      total: acc.total + (m.pay + m.da + m.hra + m.ma + m.cta),
      nps: acc.nps + m.nps,
      ptax: acc.ptax + m.ptax,
      gis: acc.gis + m.gis,
      itax: acc.itax + m.itax
    }), { pay: 0, da: 0, hra: 0, ma: 0, cta: 0, total: 0, nps: 0, ptax: 0, gis: 0, itax: 0 });

    salaryTableBody.push([
      "Total",
      totals.pay.toFixed(2),
      totals.da.toFixed(2),
      totals.hra.toFixed(2),
      totals.ma.toFixed(2),
      totals.cta.toFixed(2),
      totals.total.toFixed(2),
      totals.nps.toFixed(2),
      totals.ptax.toFixed(2),
      totals.gis.toFixed(2),
      totals.itax.toFixed(2)
    ]);

    autoTable(doc, {
      startY: 55,
      head: salaryTableHead,
      body: salaryTableBody,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5, halign: 'center' },
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], lineWidth: 0.1, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } }
    });

    // Income Tax Calculation Sheet
    currentY = (doc as any).lastAutoTable.finalY + 5;
    doc.setFontSize(10);
    doc.text("INCOME TAX CALCULATION SHEET 2025-26", pageWidth / 2, currentY, { align: 'center' });
    doc.line(margin, currentY + 2, pageWidth - margin, currentY + 2);

    currentY += 4;
    const calcLeft = [
      ["A", "Whether option for taxation u/s 115BAC?", "YES"],
      ["1.", "GROSS SALARY", `Rs. ${grossSalary.toFixed(2)}`],
      ["2.(a)", "LESS : Allowance to the extent exempt Under Section 10 (13A)", "Rs. 0.00"],
      ["(b)", "LESS : Allowance to the extent exempt Under Section 10 (14)(i)", "Rs. 0.00"],
      ["3.", "BALANCE (1-2)", `Rs. ${grossSalary.toFixed(2)}`],
      ["4.", "ADD: ANY OTHER INCOME REPORTED BY THE EMPLOYEE", "Rs. 0.00"],
      ["5.", "GROSS TOTAL INCOME (3+4)", `Rs. ${grossSalary.toFixed(2)}`],
      ["6.", "UNDER SECTION 24(1)H.B.A INT +P.TAX", `Rs. ${ptax.toFixed(2)}`],
      ["7.", "STANDARD DEDUCTION", `Rs. ${stdDed.toFixed(2)}`],
      ["8.", "INCOME CHARGEABLE UNDER THE HEAD 'SALARIES'(5-6-7)", `Rs. ${taxableIncome.toFixed(2)}`],
      ["9.", "Deductions under chapter VI-A", ""],
      ["(a)", "Section 80C (GPF/NPS/LIC/PPF/Tution Fee etc)", "Rs. 0.00"],
      ["(b)", "Section 80CCC", "Rs. 0.00"],
      ["(c)", "Section 80CCD (1)", "Rs. 0.00"],
      ["(d)", "Section 80CCD (1B)", "Rs. 0.00"],
      ["(e)", "Section 80CCD (2)", "Rs. 0.00"],
      ["(f)", "Section 80D (Health Insurance)", "Rs. 0.00"],
      ["(g)", "Section 80E (Education Loan Interest)", "Rs. 0.00"],
      ["(h)", "Section 80G (Donations)", "Rs. 0.00"],
      ["(i)", "Section 80TTA/TTB (Savings Interest)", "Rs. 0.00"],
      ["(j)", "Other Deductions", "Rs. 0.00"],
      ["10.", "AGGREGATE OF DEDUCATIBLE AMOUNT VI-A (A+B)", "0.00"]
    ];

    const calcRight = [
      ["11", "TOTAL INCOME (8-10)", `Rs. ${taxableIncome.toFixed(2)}`],
      ["12", "TOTAL INCOME", `Rs. ${taxableIncome.toFixed(2)}`],
      ["13", "TAX ON TOTAL INCOME", `Rs. ${taxOnTotal.toFixed(2)}`],
      ["14", "TAX REBATEU/S87A", `Rs. ${taxRebate.toFixed(2)}`],
      ["15", "TAX AFTER REBATE", `Rs. ${taxAfterRebate.toFixed(2)}`],
      ["16", "EDUCATION & HEALTHCESS", "Rs. 0.00"],
      ["17", "TOTAL", "Rs. 0.00"],
      ["18", "RELIEF U/S 89(I)", "Rs. 0.00"],
      ["19", "TOTALPAYABLETAX", "Rs. 0.00"],
      ["20", "TAX DEDUCTEDATSOURCE", `Rs. ${totalTaxPaid.toFixed(2)}`],
      ["21", "TAX PAYABLE/REFUNDABLE", "Rs. 0.00"]
    ];

    doc.setFontSize(6);
    const calcRowHeight = 5.5;
    const col1W = 8;
    const col3W = 28;
    const col2W = (contentWidth / 2) - col1W - col3W;

    calcLeft.forEach((row, i) => {
      const y = currentY + (i * calcRowHeight);
      // Col 1: Index
      doc.rect(margin, y, col1W, calcRowHeight);
      doc.text(row[0], margin + col1W / 2, y + 3.5, { align: 'center' });
      
      // Col 2: Description
      doc.rect(margin + col1W, y, col2W, calcRowHeight);
      let desc = row[1];
      // Reduce font size for very long descriptions
      if (doc.getTextWidth(desc) > col2W - 2) {
        doc.setFontSize(5.5);
      }
      doc.text(desc, margin + col1W + 1, y + 3.5);
      doc.setFontSize(6);

      // Col 3: Value
      doc.rect(margin + col1W + col2W, y, col3W, calcRowHeight);
      doc.text(row[2], margin + col1W + col2W + col3W - 1, y + 3.5, { align: 'right' });
    });

    calcRight.forEach((row, i) => {
      const y = currentY + (i * calcRowHeight);
      const startX = margin + contentWidth / 2;
      
      // Col 1: Index
      doc.rect(startX, y, col1W, calcRowHeight);
      doc.text(row[0], startX + col1W / 2, y + 3.5, { align: 'center' });
      
      // Col 2: Description
      doc.rect(startX + col1W, y, col2W, calcRowHeight);
      doc.text(row[1], startX + col1W + 1, y + 3.5);
      
      // Col 3: Value
      doc.rect(startX + col1W + col2W, y, col3W, calcRowHeight);
      doc.text(row[2], startX + col1W + col2W + col3W - 1, y + 3.5, { align: 'right' });
    });

    // Add the big box after item 21 on the right side
    const boxStartY = currentY + (calcRight.length * calcRowHeight);
    const boxHeight = (calcLeft.length - calcRight.length) * calcRowHeight;
    const boxWidth = contentWidth / 2;
    const boxStartX = margin + contentWidth / 2;

    doc.rect(boxStartX, boxStartY, boxWidth, boxHeight);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const [_, endYearStr] = financialYear.split('-');
    const displayYear = 2000 + parseInt(endYearStr);
    doc.text(`Tax Deduction From February, ${displayYear}-Nill`, boxStartX + boxWidth / 2, boxStartY + boxHeight / 2, { align: 'center' });

    const pageHeight2 = doc.internal.pageSize.getHeight();
    doc.setFont('helvetica', 'bold');
    doc.text("Signature of Employee", margin + 10, pageHeight2 - 15);
    doc.text(`District Programme Officer, Establishment, ${district}`, pageWidth - margin - 70, pageHeight2 - 15, { align: 'left' });

    addWatermarkToPdf(doc);
    
    const pdfDataUri = doc.output('datauristring');
    setPdfPreviewUrl(pdfDataUri);
    
    if (download) {
      doc.save(`TDS_Report_${data.profile.name.toUpperCase()}.pdf`);
      
      saveUsageData({
        section: 'TDS Automatic Generation',
        name: data.profile.name,
        panNumber: data.profile.pan
      }).catch(console.error);
    }

    if (!download) {
      setTimeout(() => {
        previewRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // PDF generation for download

  return (
    <>
    <LoadingOverlay isLoading={isProcessing} message="Extracting Data from Salary Slip..." />
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-12 animate-slideUp">
      <div className="bg-white rounded-[2rem] md:rounded-[3rem] shadow-2xl border border-gray-100 overflow-hidden mb-8">
        <div className="bg-gradient-to-br from-orange-600 to-red-700 p-6 md:p-10 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 md:p-12 opacity-10 pointer-events-none"><Calculator size={100} className="md:w-[140px] md:h-[140px]" /></div>
          <div className="flex flex-col md:flex-row justify-between items-start relative z-10 gap-4 md:gap-0">
            <div className="flex items-center space-x-4 md:space-x-6">
              <div className="bg-white/20 p-3 md:p-5 rounded-2xl md:rounded-3xl backdrop-blur-md border border-white/30 shadow-2xl"><FileText size={32} className="md:w-12 md:h-12 text-white" /></div>
              <div>
                <h2 className="text-xl md:text-4xl font-black tracking-tight mb-1 uppercase">TDS Information Portal</h2>
                <p className="text-orange-100 font-bold uppercase text-[8px] md:text-[10px] tracking-[0.4em]">Automated Income Tax & Salary Report</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 md:p-12">
          {!data ? (
            <div className="max-w-2xl mx-auto text-center space-y-8">
               <div className="w-20 h-20 md:w-24 md:h-24 bg-orange-50 rounded-[2rem] flex items-center justify-center mx-auto text-orange-600 shadow-inner">
                 <FileUp size={40} />
               </div>
               <div className="space-y-4">
                 <h3 className="text-2xl md:text-3xl font-black text-gray-900 uppercase tracking-tight">Upload Yearly Salary Slip</h3>
                 <p className="text-gray-500 font-medium">Select Financial Year and upload your HRMS Yearly Salary Slip (Page 2) to automatically generate your TDS Certificate and Income Statement.</p>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                 <div className="text-left">
                   <label className="text-[10px] font-black text-orange-400 uppercase tracking-widest ml-1">School Name</label>
                   <input 
                     value={schoolName}
                     onChange={(e) => setSchoolName(e.target.value)}
                     className="w-full px-5 py-3 bg-white border-2 border-orange-100 rounded-xl font-bold text-gray-800 focus:border-orange-500 outline-none transition-all"
                     placeholder="Enter School Name"
                   />
                 </div>
                 <div className="text-left">
                   <label className="text-[10px] font-black text-orange-400 uppercase tracking-widest ml-1">Joining Date</label>
                   <input 
                     type="date"
                     value={joiningDate}
                     onChange={(e) => setJoiningDate(e.target.value)}
                     className="w-full px-5 py-3 bg-white border-2 border-orange-100 rounded-xl font-bold text-gray-800 focus:border-orange-500 outline-none transition-all"
                   />
                 </div>
                 <div className="text-left md:col-span-2">
                   <label className="text-[10px] font-black text-orange-400 uppercase tracking-widest ml-1">Financial Year</label>
                   <select 
                     value={financialYear}
                     onChange={(e) => setFinancialYear(e.target.value)}
                     className="w-full px-5 py-3 bg-white border-2 border-orange-100 rounded-xl font-bold text-gray-800 focus:border-orange-500 outline-none transition-all"
                   >
                     <option value="2024-25">2024-25</option>
                     <option value="2025-26">2025-26</option>
                     <option value="2026-27">2026-27</option>
                   </select>
                 </div>
               </div>

               <label className="relative group cursor-pointer inline-block w-full">
                 <input 
                   type="file" 
                   accept=".pdf" 
                   onChange={handleFileUpload}
                   className="hidden"
                   disabled={isProcessing}
                 />
                 <div className={`
                   w-full py-6 md:py-8 rounded-2xl md:rounded-[2.5rem] font-black text-xl md:text-2xl transition-all flex items-center justify-center gap-4 shadow-xl
                   ${isProcessing 
                     ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                     : 'bg-slate-900 text-white hover:bg-black hover:scale-[1.02] active:scale-95'
                   }
                 `}>
                   {isProcessing ? (
                     <RefreshCw size={28} className="animate-spin" />
                   ) : (
                     <Sparkles size={28} />
                   )}
                   {isProcessing ? 'EXTRACTING DATA...' : 'SELECT PDF FILE'}
                 </div>
               </label>

               {error && (
                 <div className="p-4 bg-red-50 text-red-600 rounded-2xl flex items-center gap-3 justify-center border border-red-100 font-bold">
                   <AlertCircle size={20} />
                   <span>{error}</span>
                 </div>
               )}
            </div>
          ) : (
            <div className="space-y-10 animate-fadeIn">
              {/* Hidden Tab Switcher as requested */}
              <div className="hidden flex p-1 bg-slate-100 rounded-2xl w-full md:w-fit mx-auto">
                <button 
                  onClick={() => setActiveTab('summary')}
                  className={`flex-1 md:flex-none px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'summary' ? 'bg-white text-orange-600 shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  Summary Preview
                </button>
                <button 
                  onClick={() => setActiveTab('full')}
                  className={`flex-1 md:flex-none px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'full' ? 'bg-white text-orange-600 shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  Full Page 2 Content
                </button>
              </div>

              {/* Hidden Data Extraction Summary as requested */}
              <div className="hidden">
                <AnimatePresence mode="wait">
                  {activeTab === 'summary' ? (
                    <motion.div 
                      key="summary"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-10"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-slate-50 p-6 md:p-8 rounded-3xl border border-slate-100 space-y-4">
                          <div className="flex items-center space-x-3 text-orange-600 mb-2">
                            <CheckCircle2 size={24} />
                            <h4 className="font-black uppercase tracking-widest text-sm">Extracted Information</h4>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between border-b border-slate-200 pb-2">
                              <span className="text-xs font-bold text-slate-400 uppercase">Name</span>
                              <span className="font-black text-slate-900">{data.profile.name}</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-200 pb-2">
                              <span className="text-xs font-bold text-slate-400 uppercase">PAN</span>
                              <span className="font-black text-slate-900">{data.profile.pan}</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-200 pb-2">
                              <span className="text-xs font-bold text-slate-400 uppercase">District</span>
                              <span className="font-black text-slate-900">{data.profile.district}</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-200 pb-2">
                              <span className="text-xs font-bold text-slate-400 uppercase">Designation</span>
                              <span className="font-black text-slate-900">{data.profile.designation}</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-200 pb-2">
                              <span className="text-xs font-bold text-slate-400 uppercase">Class Range</span>
                              <span className="font-black text-slate-900">{data.profile.classRange}</span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-slate-50 p-6 md:p-8 rounded-3xl border border-slate-100 space-y-4">
                          <div className="flex items-center space-x-3 text-orange-600 mb-2">
                            <CheckCircle2 size={24} />
                            <h4 className="font-black uppercase tracking-widest text-sm">Deductor Information</h4>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between border-b border-slate-200 pb-2">
                              <span className="text-xs font-bold text-slate-400 uppercase">Name</span>
                              <span className="font-black text-slate-900 text-right">{deductorInfo ? formatDeductorValue(deductorInfo.address) : 'Loading...'}</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-200 pb-2">
                              <span className="text-xs font-bold text-slate-400 uppercase">PAN</span>
                              <span className="font-black text-slate-900">{deductorInfo ? formatDeductorValue(deductorInfo.pan) : 'Loading...'}</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-200 pb-2">
                              <span className="text-xs font-bold text-slate-400 uppercase">TAN</span>
                              <span className="font-black text-slate-900">{deductorInfo ? formatDeductorValue(deductorInfo.tan) : 'Loading...'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-orange-50 p-6 md:p-8 rounded-3xl border border-orange-100 space-y-4">
                          <div className="flex items-center space-x-3 text-orange-600 mb-2">
                            <School size={24} />
                            <h4 className="font-black uppercase tracking-widest text-sm">Additional Details</h4>
                          </div>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-orange-400 uppercase tracking-widest ml-1">School Name</label>
                              <input 
                                value={schoolName}
                                onChange={(e) => setSchoolName(e.target.value.toUpperCase())}
                                className="w-full px-5 py-3 bg-white border-2 border-orange-100 rounded-xl font-bold text-gray-800 focus:border-orange-500 outline-none transition-all uppercase"
                                placeholder="ENTER SCHOOL NAME"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-orange-400 uppercase tracking-widest ml-1">Joining Date</label>
                              <input 
                                type="date"
                                value={joiningDate}
                                onChange={(e) => setJoiningDate(e.target.value)}
                                className="w-full px-5 py-3 bg-white border-2 border-orange-100 rounded-xl font-bold text-gray-800 focus:border-orange-500 outline-none transition-all"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-100 shadow-inner overflow-hidden">
                        <div className="flex items-center justify-between mb-6">
                          <h4 className="font-black uppercase tracking-widest text-sm text-slate-900">Monthly Salary Details</h4>
                          <span className="text-[10px] font-black text-orange-500 bg-orange-50 px-3 py-1 rounded-full uppercase tracking-widest">March to February</span>
                        </div>
                        
                        <div className="overflow-x-auto -mx-6 md:-mx-8 px-6 md:px-8">
                          <table className="w-full text-left border-collapse min-w-[800px]">
                            <thead>
                              <tr className="bg-slate-50 text-slate-500 uppercase text-[10px] font-black tracking-widest border-y border-slate-100">
                                <th className="py-4 px-4 first:rounded-l-xl">Month</th>
                                <th className="py-4 px-4">Pay</th>
                                <th className="py-4 px-4 text-orange-600">DA</th>
                                <th className="py-4 px-4">HRA</th>
                                <th className="py-4 px-4">MA</th>
                                <th className="py-4 px-4">CTA</th>
                                <th className="py-4 px-4 text-blue-600">NPS</th>
                                <th className="py-4 px-4">PTAX</th>
                                <th className="py-4 px-4">GIS</th>
                                <th className="py-4 px-4 last:rounded-r-xl">ITAX</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                              {data.monthlyDrawn.map((m, i) => (
                                <tr key={i} className="group hover:bg-slate-50/50 transition-colors font-bold text-slate-700 text-xs">
                                  <td className="py-4 px-4 text-slate-900 font-black">{m.month}</td>
                                  <td className="py-4 px-4 font-mono">{m.pay.toLocaleString()}</td>
                                  <td className="py-4 px-4 font-mono text-orange-600">{m.da.toLocaleString()}</td>
                                  <td className="py-4 px-4 font-mono">{m.hra.toLocaleString()}</td>
                                  <td className="py-4 px-4 font-mono">{m.ma.toLocaleString()}</td>
                                  <td className="py-4 px-4 font-mono">{m.cta.toLocaleString()}</td>
                                  <td className="py-4 px-4 font-mono text-blue-600">{m.nps.toLocaleString()}</td>
                                  <td className="py-4 px-4 font-mono">{m.ptax.toLocaleString()}</td>
                                  <td className="py-4 px-4 font-mono">{m.gis.toLocaleString()}</td>
                                  <td className="py-4 px-4 font-mono">{m.itax.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div 
                      key="full"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-slate-900 p-6 md:p-10 rounded-3xl border border-slate-800 shadow-2xl relative"
                    >
                      <div className="absolute top-4 right-6 flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                        <span className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">Raw Data Stream</span>
                      </div>
                      <h4 className="font-black uppercase tracking-widest text-xs text-slate-500 mb-6">Full Page 2 Extraction Output</h4>
                      <div className="bg-black/40 p-6 md:p-8 rounded-2xl border border-white/5 h-[400px] md:h-[600px] overflow-y-auto no-scrollbar">
                        <pre className="text-emerald-400/90 font-mono text-[10px] md:text-sm leading-relaxed whitespace-pre-wrap">
                          {data.rawPage2Text}
                        </pre>
                      </div>
                      <div className="mt-6 flex items-center gap-3 text-slate-500">
                        <AlertCircle size={14} />
                        <p className="text-[9px] font-bold uppercase tracking-wide">This is the raw text extracted directly from the PDF using the Arrear Engine.</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                <button 
                  onClick={() => setData(null)}
                  className="flex-1 py-6 rounded-2xl md:rounded-[2.5rem] bg-white border-2 border-slate-100 text-slate-500 font-black text-lg flex items-center justify-center shadow-md active:scale-95"
                >
                  <RefreshCw className="mr-3" /> RE-UPLOAD
                </button>
                <button 
                  onClick={() => generatePDF(true)}
                  className="flex-[2] py-6 rounded-2xl md:rounded-[2.5rem] bg-gradient-to-r from-orange-600 to-red-600 text-white font-black text-xl md:text-2xl shadow-2xl transition-all flex items-center justify-center transform active:scale-[0.97]"
                >
                  <Download className="mr-4" size={28} /> DOWNLOAD REPORT
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden TDS Report Preview as requested */}
      {pdfPreviewUrl && (
        <div ref={previewRef} className="hidden animate-slideUp scroll-mt-28">
          <div className="bg-white rounded-[2.5rem] md:rounded-[4rem] shadow-2xl border-4 border-slate-100 overflow-hidden">
            <div className="bg-slate-900 p-6 md:p-10 text-white flex justify-between items-center">
              <h3 className="text-xl md:text-3xl font-black tracking-tight uppercase">TDS Report Preview</h3>
              <button 
                onClick={() => setPdfPreviewUrl(null)} 
                className="bg-red-500/10 p-3 rounded-full text-red-500 hover:bg-red-500/20 transition-all"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-4 md:p-12 bg-slate-100">
              <iframe src={pdfPreviewUrl} className="w-full h-[600px] md:h-[950px] border-none rounded-2xl md:rounded-[3rem] bg-white shadow-inner"></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};
